import { AgGridReact } from 'ag-grid-react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Button, Row, Col } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons'
import {
  fetchSlackChannels,
  deleteSlackChannel,
  setSlackChannelModal,
} from 'store/SlackChannels/actions'
import SlackChannelModal from './SlackChannelModal'
import moment from 'moment'
import { setAreYouSureModalData } from 'store/Recruitment/actions'

function SlackChannels() {
  const dispatch = useDispatch()
  const { channels, loading, deleteLoading } = useSelector(
    (store) => store.slackChannels
  )

  const [gridApi, setGridApi] = useState(null)

  useEffect(() => {
    dispatch(fetchSlackChannels())
  }, [dispatch])

  const onGridReady = (params) => {
    setGridApi(params.api)
  }

  const handleEdit = (data) => {
    dispatch(
      setSlackChannelModal({
        show: true,
        mode: 'edit',
        data: data,
      })
    )
  }

  const handleDelete = (data) => {
    dispatch(
      setAreYouSureModalData({
        show: true,
        text: `Are you sure you want to delete the channel "${data.name}"? This action cannot be undone.`,
        customFn: () => {
          dispatch(deleteSlackChannel(data.id))
        },
      })
    )
  }

  const handleAddNew = () => {
    dispatch(
      setSlackChannelModal({
        show: true,
        mode: 'create',
        data: null,
      })
    )
  }

  const ActionCellRenderer = (params) => {
    return (
      <div className='d-flex align-items-center h-100' style={{ gap: '8px' }}>
        <Button
          color='info'
          size='sm'
          onClick={() => handleEdit(params.data)}
          title='Edit Channel'
          style={{ minWidth: '32px', padding: '4px 8px' }}
        >
          <FontAwesomeIcon icon={faPencil} />
        </Button>
        <Button
          color='danger'
          size='sm'
          onClick={() => handleDelete(params.data)}
          title='Delete Channel'
          disabled={deleteLoading}
          style={{ minWidth: '32px', padding: '4px 8px' }}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
    )
  }

  const columns = [
    {
      headerName: 'Channel Name',
      field: 'name',
      flex: 1,
      minWidth: 200,
      cellStyle: { fontWeight: '500' },
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 1.5,
      minWidth: 250,
      cellRenderer: (params) => params.value || 'No description provided',
      cellStyle: { color: '#6c757d', fontStyle: 'italic' },
    },
    {
      headerName: 'Webhook URL',
      field: 'webhook_url',
      flex: 2,
      minWidth: 300,
      cellRenderer: (params) => (
        <span
          title={params.value}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            color: '#495057',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
          }}
        >
          {params.value}
        </span>
      ),
    },

    {
      headerName: 'Actions',
      field: 'actions',
      width: 120,
      cellRenderer: ActionCellRenderer,
      sortable: false,
      filter: false,
      suppressMenu: true,
      cellStyle: { display: 'flex', alignItems: 'center', padding: '4px' },
    },
  ]

  const defaultColDef = {
    sortable: true,
    resizable: true,
  }

  return (
    <>
      <Card className='mt--6 mx-3' style={{ minHeight: '85vh' }}>
        <div className='card-header border-0 d-flex align-items-center justify-content-between'>
          <Row className='align-items-center w-100'>
            <Col>
              <h3 className='mb-0'>Slack Channels Management</h3>
            </Col>
            <Col xs='auto'>
              <Button
                color='info'
                onClick={handleAddNew}
                className='d-flex align-items-center'
                style={{ gap: '8px' }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Channel
              </Button>
            </Col>
          </Row>
        </div>
        <div className='card-body pt-0'>
          {loading ? (
            <div
              className='d-flex justify-content-center align-items-center'
              style={{ height: '400px' }}
            >
              <div className='spinner-border text-primary' role='status'>
                <span className='sr-only'>Loading...</span>
              </div>
            </div>
          ) : (
            <div
              className='ag-theme-alpine'
              style={{ height: 600, width: '100%' }}
            >
              <AgGridReact
                columnDefs={columns}
                rowData={channels}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                pagination={true}
                paginationPageSize={20}
                rowHeight={50}
                headerHeight={45}
                animateRows={true}
                suppressRowClickSelection={true}
                getRowId={(params) => params.data.id}
                overlayNoRowsTemplate={
                  '<span style="color: #999; font-size: 16px;">No Slack channels configured. Click "Add Channel" to create your first one.</span>'
                }
              />
            </div>
          )}
        </div>
      </Card>
      <SlackChannelModal />
    </>
  )
}

export default SlackChannels
